import { type MouseEvent } from 'react';
import { useTheme } from '@emotion/react';
import { CircleNotch, Warning } from '@phosphor-icons/react';
import { EventType } from 'common/EventConstants';
import { useApi } from 'client/lib/trpc';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { useDeploymentWarning } from 'client/pages/deployment/useDeploymentWarning';
import { DropdownMenu } from 'client/components/DropdownMenu';

interface DevelopButtonBlockerProps {
  target: string;
  startingWarning: string;
  stoppedWarning: string;
}

export const useDevelopBlocker = ({ target, startingWarning, stoppedWarning }: DevelopButtonBlockerProps) => {
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const statusTree = useDeploymentStatusTree(deploymentID);
  const theme = useTheme();
  const api = useApi();

  // Check if all containers belonging to "diploi" are green
  const containers = (statusTree?.containers.items || []).filter((item) => item.namespace === 'diploi');
  const { isWarningDisplayed, isErrorDisplayed } = useDeploymentWarning({ containers });

  const handleInactiveLog = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!deploymentID) return;

    if (target === 'browser') {
      api.status.eventCreate.mutate({
        eventTypeID: EventType.DEPLOYMENT_BROWSER_IDE_INACTIVE,
        deploymentID,
        reason: isErrorDisplayed ? 'disabled' : 'starting',
      });
    }
  };

  const menuWarningItems = [
    <DropdownMenu.Banner
      key="starting"
      title="Deployment is Starting"
      subtitle={startingWarning}
      icon={<Warning color={theme.color.yellow[100]} />}
      ornament={<CircleNotch color={theme.color.yellow[100]} />}
      state="loading"
    />,
  ];

  const menuErrorItems = [
    <DropdownMenu.Banner
      key="stopped"
      title="Deployment is Stopped"
      subtitle={stoppedWarning}
      icon={<Warning color={theme.color.yellow[100]} />}
    />,
  ];

  const menuItems = isErrorDisplayed ? menuErrorItems : menuWarningItems;

  return { menuItems, isWarningDisplayed, isErrorDisplayed, handleInactiveLog };
};
